import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "availableBalance",
    "paymentAmount", 
    "progressBar",
    "validationCard",
    "validationMessage",
    "currentBalance"
  ]

  static values = {
    maxAmount: Number,
    invoiceBalance: Number,
    currentPaymentAmount: Number
  }

  connect() {
    this.updateDisplay()
    this.validateForm()
  }

  validateDate(event) {
    const selectedDate = new Date(event.target.value)
    const today = new Date()
    
    if (selectedDate > today) {
      this.showValidationError("La fecha de pago no puede ser futura")
      event.target.classList.add("input-error")
    } else {
      event.target.classList.remove("input-error")
      this.hideValidationError()
    }
  }

  calculateAllocations(event) {
    const paymentAmount = parseFloat(event.target.value) || 0
    this.currentPaymentAmountValue = paymentAmount
    
    // Update display
    this.updatePaymentAmount(paymentAmount)
    this.updateProgressBar(paymentAmount)
    this.validatePaymentAmount(paymentAmount)
    
    // Auto-distribute to allocations if they exist
    this.autoDistributeToAllocations(paymentAmount)
  }

  updateAllocationTotal() {
    const allocationInputs = this.element.querySelectorAll('[data-payment-allocations-target="lines"] input[type="number"]')
    let total = 0
    
    allocationInputs.forEach(input => {
      if (!input.closest('.payment-allocation-line').querySelector('input[name*="_destroy"]')?.checked) {
        total += parseFloat(input.value) || 0
      }
    })
    
    // Update total display
    const totalTarget = this.element.querySelector('[data-payment-allocations-target="total"]')
    if (totalTarget) {
      totalTarget.textContent = this.formatCurrency(total)
    }
    
    // Validate that allocations don't exceed payment amount
    this.validateAllocations(total)
  }

  addAllocation() {
    // This would typically clone an existing allocation line
    // For now, we'll show a message that manual addition isn't implemented
    alert("Para agregar líneas manualmente, use el formulario de nueva factura o edite las existentes.")
  }

  validateSubmit(event) {
    const isValid = this.validateForm()
    
    if (!isValid) {
      event.preventDefault()
      this.showValidationError("Por favor corrija los errores antes de continuar")
    }
  }

  // Private methods

  updateDisplay() {
    const paymentAmountInput = this.element.querySelector('input[name*="[amount]"]')
    if (paymentAmountInput) {
      const amount = parseFloat(paymentAmountInput.value) || 0
      this.updatePaymentAmount(amount)
      this.updateProgressBar(amount)
    }
    
    this.updateAllocationTotal()
  }

  updatePaymentAmount(amount) {
    if (this.hasPaymentAmountTarget) {
      this.paymentAmountTarget.textContent = this.formatCurrency(amount)
    }
  }

  updateProgressBar(amount) {
    if (this.hasProgressBarTarget && this.invoiceBalanceValue > 0) {
      const percentage = Math.min((amount / this.invoiceBalanceValue) * 100, 100)
      this.progressBarTarget.value = percentage
    }
  }

  validatePaymentAmount(amount) {
    const maxAllowed = this.invoiceBalanceValue + (this.currentPaymentAmountValue || 0)
    
    if (amount > maxAllowed) {
      this.showValidationError(`El monto no puede exceder ${this.formatCurrency(maxAllowed)}`)
      return false
    } else if (amount <= 0) {
      this.showValidationError("El monto debe ser mayor a cero")
      return false
    }
    
    return true
  }

  validateAllocations(total) {
    const paymentAmountInput = this.element.querySelector('input[name*="[amount]"]')
    const paymentAmount = parseFloat(paymentAmountInput?.value) || 0
    
    if (total > paymentAmount) {
      this.showValidationError(`Las asignaciones (${this.formatCurrency(total)}) no pueden exceder el monto del pago (${this.formatCurrency(paymentAmount)})`)
      return false
    }
    
    return true
  }

  validateForm() {
    let isValid = true
    
    // Validate payment date
    const dateInput = this.element.querySelector('input[name*="[payment_date]"]')
    if (dateInput) {
      const selectedDate = new Date(dateInput.value)
      const today = new Date()
      
      if (selectedDate > today) {
        isValid = false
      }
    }
    
    // Validate payment amount
    const amountInput = this.element.querySelector('input[name*="[amount]"]')
    if (amountInput) {
      const amount = parseFloat(amountInput.value) || 0
      if (!this.validatePaymentAmount(amount)) {
        isValid = false
      }
    }
    
    // Validate payment method
    const methodSelect = this.element.querySelector('select[name*="[payment_method]"]')
    if (methodSelect && !methodSelect.value) {
      isValid = false
    }
    
    // Validate allocations
    this.updateAllocationTotal()
    const totalElement = this.element.querySelector('[data-payment-allocations-target="total"]')
    if (totalElement) {
      const total = this.parseCurrency(totalElement.textContent)
      if (!this.validateAllocations(total)) {
        isValid = false
      }
    }
    
    if (isValid) {
      this.hideValidationError()
    }
    
    return isValid
  }

  autoDistributeToAllocations(paymentAmount) {
    const allocationInputs = this.element.querySelectorAll('[data-payment-allocations-target="lines"] input[type="number"]')
    
    if (allocationInputs.length === 0) return
    
    let remainingAmount = paymentAmount
    const activeInputs = Array.from(allocationInputs).filter(input => 
      !input.closest('.payment-allocation-line').querySelector('input[name*="_destroy"]')?.checked
    )
    
    activeInputs.forEach((input, index) => {
      if (remainingAmount <= 0) {
        input.value = "0.00"
        return
      }
      
      // Get the maximum amount for this line
      const maxAmountElement = input.closest('.payment-allocation-line').querySelector('.input.bg-base-200')
      const maxAmount = maxAmountElement ? this.parseCurrency(maxAmountElement.textContent) : Infinity
      
      // Calculate allocation amount
      let allocationAmount
      if (index === activeInputs.length - 1) {
        // Last input gets remaining amount
        allocationAmount = Math.min(remainingAmount, maxAmount)
      } else {
        // Distribute evenly among remaining inputs
        const remainingInputs = activeInputs.length - index
        allocationAmount = Math.min(remainingAmount / remainingInputs, maxAmount)
      }
      
      input.value = allocationAmount.toFixed(2)
      remainingAmount -= allocationAmount
    })
    
    this.updateAllocationTotal()
  }

  showValidationError(message) {
    if (this.hasValidationCardTarget && this.hasValidationMessageTarget) {
      this.validationMessageTarget.textContent = message
      this.validationCardTarget.style.display = "block"
    }
  }

  hideValidationError() {
    if (this.hasValidationCardTarget) {
      this.validationCardTarget.style.display = "none"
    }
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 2
    }).format(amount)
  }

  parseCurrency(text) {
    // Remove currency symbols and parse as float
    return parseFloat(text.replace(/[^\d.-]/g, '')) || 0
  }
}